import { useEffect, useState, useCallback, useMemo } from "react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Wallet, Trash, DownloadSimple, CaretLeft, CaretRight, FileText } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import usePullToRefresh from "@/lib/usePullToRefresh";
import PullToRefreshIndicator from "@/components/PullToRefreshIndicator";
import PaymentDeleteDialog from "@/components/PaymentDeleteDialog";
import { useInvoiceViewer } from "@/components/InvoiceViewer";
import { useSort, sortRows, SortHeader } from "@/components/SortHeader";

const PAGE_SIZE = 50;
const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const fmtDateTime = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
};
const fmtMonthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
};
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const CSV_HEADERS_EXEC = ["Date", "Creator", "Brand", "Campaign", "Amount (INR)", "Payment type", "Mode", "Reference", "Logged by"];
const CSV_HEADERS_ADMIN = [...CSV_HEADERS_EXEC, "TDS", "GST", "GST reimbursed?"];

const csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const downloadCsv = (headers, rows, filename) => {
  const csv = [headers.join(","), ...rows.map((r) => r.map(csvEscape).join(","))].join("\r\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export default function Payments() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { openInvoice } = useInvoiceViewer();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const [month, setMonth] = useState(currentMonth());
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("All"); // "All" | "GST" | "Non-GST"
  const [sort, toggleSort] = useSort("paid_at");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payments/recent", { params: { limit: 500 } });
      setItems(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Reset to page 1 whenever the active month or filter changes
  useEffect(() => { setPage(1); }, [month, typeFilter, sort]);

  const ptr = usePullToRefresh(load);

  const filtered = useMemo(() => {
    return sortRows(items.filter((p) => {
      if (!p.paid_at) return false;
      const d = new Date(p.paid_at);
      if (isNaN(d.getTime())) return false;
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (ym !== month) return false;
      if (typeFilter === "GST") return (p.invoice_type || "").toLowerCase() === "gst";
      if (typeFilter === "Non-GST") return (p.invoice_type || "").toLowerCase() === "non-gst";
      return true;
    }), (p) => new Date(p.paid_at).toISOString(), sort.dir);
  }, [items, month, typeFilter, sort]);

  // Monthly totals (always full filtered set, not just visible page)
  const totals = useMemo(() => {
    return filtered.reduce((acc, p) => {
      acc.paid += Number(p.amount || 0);
      acc.tds += Number(p.tds_withheld || 0);
      acc.gst += Number(p.gst_pending || 0);
      return acc;
    }, { paid: 0, tds: 0, gst: 0 });
  }, [filtered]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = useMemo(
    () => filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filtered, safePage]
  );

  const exportCsv = () => {
    const headers = isAdmin ? CSV_HEADERS_ADMIN : CSV_HEADERS_EXEC;
    const rows = filtered.map((p) => {
      const base = [
        p.paid_at, p.source === "Cost" ? "Additional Cost" : "Creator",
        p.creator_username, p.brand_name, p.campaign_name,
        p.amount, p.payment_type, p.mode, p.reference, p.created_by_name,
      ];
      if (isAdmin) {
        base.push(
          p.tds_withheld || 0,
          p.gst_pending || 0,
          p.gst_pending ? (p.gst_reimbursed ? `Yes${p.gst_reimbursed_at ? ` (${p.gst_reimbursed_at})` : ""}` : "No") : "",
        );
      }
      return base;
    });
    downloadCsv(headers, rows, `payment-log_${month}.csv`);
  };

  const handleDeleted = async () => {
    await load();
    // Stay on current page if still populated, else bump back one
    if ((filtered.length - 1) <= (safePage - 1) * PAGE_SIZE && safePage > 1) setPage(safePage - 1);
  };

  return (
    <div className="space-y-5" data-testid="payments-page">
      <PullToRefreshIndicator {...ptr} />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Ledger</div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Payment log</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Monthly ledger of creator payouts and additional cost payments.</p>
        </div>
        <Button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          variant="outline"
          className="border-slate-300 shrink-0"
          data-testid="payments-export-csv-btn"
        >
          <DownloadSimple size={14} className="mr-1.5" /> Export CSV ({filtered.length})
        </Button>
      </div>

      <Card className="p-3 sm:p-4 border border-slate-200 shadow-none" data-testid="payments-month-card">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMonth(shiftMonth(month, -1))}
            className="h-9 border-slate-300"
            data-testid="month-prev-btn"
          >
            <CaretLeft size={14} className="mr-1" /> Prev
          </Button>
          <div className="text-sm font-semibold text-slate-900 min-w-[160px] text-center" data-testid="month-label">
            {fmtMonthLabel(month)}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMonth(shiftMonth(month, 1))}
            className="h-9 border-slate-300"
            data-testid="month-next-btn"
          >
            Next <CaretRight size={14} className="ml-1" />
          </Button>
          {month !== currentMonth() && (
            <Button variant="ghost" size="sm" onClick={() => setMonth(currentMonth())} className="h-9 text-xs text-slate-600" data-testid="month-today-btn">
              Jump to current month
            </Button>
          )}
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" role="tablist" aria-label="Invoice type filter" data-testid="type-filter">
            {["All", "GST", "Non-GST"].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setTypeFilter(v)}
                className={`text-xs px-3 py-1.5 transition-colors ${typeFilter === v ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                data-testid={`type-filter-${v.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="ml-auto grid grid-cols-3 gap-3 text-xs min-w-[280px]">
            <div className="text-right" data-testid="month-total-paid">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Total paid</div>
              <div className="font-mono font-semibold text-slate-900">{fmtCur(totals.paid)}</div>
            </div>
            {isAdmin && (
              <>
                <div className="text-right" data-testid="month-total-tds">
                  <div className="text-[10px] uppercase tracking-wider text-amber-700">TDS</div>
                  <div className="font-mono font-semibold text-amber-800">{fmtCur(totals.tds)}</div>
                </div>
                <div className="text-right" data-testid="month-total-gst">
                  <div className="text-[10px] uppercase tracking-wider text-indigo-700">GST</div>
                  <div className="font-mono font-semibold text-indigo-800">{fmtCur(totals.gst)}</div>
                </div>
              </>
            )}
          </div>
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50 sticky top-0">
              <TableRow>
                <TableHead><SortHeader label="Date" sortKey="paid_at" sort={sort} onToggle={toggleSort} testid="payments-sort-date" /></TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Creator / Cost</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Invoice #</TableHead>
                {isAdmin && <TableHead className="text-right">TDS</TableHead>}
                {isAdmin && <TableHead className="text-right">GST</TableHead>}
                <TableHead>Mode</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Logged by</TableHead>
                {isAdmin && <TableHead className="text-right w-[60px]">·</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={isAdmin ? 14 : 11} className="text-center text-slate-500 py-10">Loading…</TableCell></TableRow>
              ) : pageItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 14 : 11} className="text-center py-12">
                    <Wallet size={28} className="mx-auto text-slate-300 mb-2" weight="duotone" />
                    <div className="text-sm text-slate-500">
                      No payments in {fmtMonthLabel(month)}. Use the arrows to browse other months.
                    </div>
                  </TableCell>
                </TableRow>
              ) : pageItems.map((p) => (
                <TableRow key={`${p.source || "Creator"}-${p.id}`} className="hover:bg-slate-50/60" data-testid={`payment-row-${p.id}`}>
                  <TableCell className="text-xs font-mono whitespace-nowrap">{fmtDateTime(p.paid_at)}</TableCell>
                  <TableCell>
                    <span className={`inline-block text-[10px] rounded-full px-2 py-0.5 font-semibold border ${p.source === "Cost" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                      {p.source === "Cost" ? "Cost" : "Creator"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => p.campaign_id && navigate(`/campaigns/${p.campaign_id}`)}
                      className="text-sm text-slate-900 hover:text-blue-600 font-medium text-left"
                    >
                      {p.source === "Cost" ? (p.creator_username || "—") : `@${p.creator_username || "—"}`}
                    </button>
                    {p.source === "Cost" && p.cost_poc_name && (
                      <div className="text-[11px] text-slate-500 mt-0.5">{p.cost_poc_name}</div>
                    )}
                    {(p.invoice_type || "").toLowerCase() === "gst" && p.legal_name && (
                      <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`row-legal-name-${p.id}`}>{p.legal_name}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-700">
                    <div className="font-semibold text-slate-900">{p.brand_name || "—"}</div>
                    {p.campaign_name && <div className="text-[11px] text-slate-500">{p.campaign_name}</div>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm font-semibold text-slate-900">{fmtCur(p.amount)}</TableCell>
                  <TableCell>
                    <span className={`text-[11px] rounded-full px-2 py-0.5 font-medium ${p.payment_type === "Full" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {p.payment_type}
                    </span>
                  </TableCell>
                  <TableCell data-testid={`row-invoice-${p.id}`}>
                    {p.invoice_file_id ? (
                      <button
                        type="button"
                        onClick={() => openInvoice(p.invoice_file_id, p.invoice_filename)}
                        className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 text-xs"
                        data-testid={`payment-invoice-link-${p.id}`}
                      >
                        <FileText size={11} /> {p.invoice_type || "Invoice"}
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400">{p.invoice_type || "—"}</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-[11px] text-slate-700" data-testid={`row-invoice-number-${p.id}`}>
                    {(p.invoice_type || "").toLowerCase() === "gst" ? (p.invoice_number || <span className="text-slate-400">—</span>) : <span className="text-slate-400">—</span>}
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right font-mono text-xs text-amber-700" data-testid={`row-tds-${p.id}`}>
                      {Number(p.tds_withheld || 0) > 0 ? fmtCur(p.tds_withheld) : <span className="text-slate-300">—</span>}
                    </TableCell>
                  )}
                  {isAdmin && (
                    <TableCell className="text-right font-mono text-xs" data-testid={`row-gst-${p.id}`}>
                      {Number(p.gst_pending || 0) > 0 ? (
                        p.gst_reimbursed ? (
                          <div className="flex flex-col items-end gap-0.5" title={`Reimbursed · ${p.gst_reimbursed_ref || "no reference"}`}>
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Paid
                            </span>
                            {(p.gst_reimbursed_at || p.gst_reimbursed_ref) && (
                              <span className="text-[9px] uppercase tracking-wider text-emerald-700/70">
                                {p.gst_reimbursed_at ? new Date(p.gst_reimbursed_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                                {p.gst_reimbursed_ref && (p.gst_reimbursed_at ? " · " : "") + p.gst_reimbursed_ref}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-rose-600 font-semibold" title="GST reimbursement pending">{fmtCur(p.gst_pending)}</span>
                        )
                      ) : <span className="text-slate-300">—</span>}
                    </TableCell>
                  )}
                  <TableCell className="text-xs text-slate-700" data-testid={`row-mode-${p.id}`}>{p.mode || "—"}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-500" data-testid={`row-reference-${p.id}`}>
                    <div className="flex items-center gap-1.5">
                      <span>{p.reference || "—"}</span>
                      {p.release_proof_file_id && (
                        <button
                          type="button"
                          onClick={() => openInvoice(p.release_proof_file_id, p.release_proof_filename || "Payment proof")}
                          className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-0.5"
                          title="View payment screenshot"
                          data-testid={`row-proof-${p.id}`}
                        >
                          <FileText size={11} />
                        </button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{p.created_by_name || "—"}</TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <button
                        onClick={() => setToDelete(p)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                        title="Delete payment"
                        data-testid={`delete-payment-${p.id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {filtered.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-t border-slate-200 bg-slate-50/60 text-xs text-slate-600" data-testid="pager">
            <span>
              Showing <b>{(safePage - 1) * PAGE_SIZE + 1}</b>–<b>{Math.min(safePage * PAGE_SIZE, filtered.length)}</b> of <b>{filtered.length}</b> · Total {fmtCur(totals.paid)}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="h-8 border-slate-300"
                data-testid="pager-prev-btn"
              >
                <CaretLeft size={12} className="mr-1" /> Previous
              </Button>
              <span className="font-mono" data-testid="pager-position">Page {safePage} of {pageCount}</span>
              <Button
                variant="outline" size="sm"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={safePage >= pageCount}
                className="h-8 border-slate-300"
                data-testid="pager-next-btn"
              >
                Next <CaretRight size={12} className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <PaymentDeleteDialog
        payment={toDelete}
        onOpenChange={setToDelete}
        onDeleted={handleDeleted}
      />
    </div>
  );
}
